/* eslint-disable max-len, max-lines */
import './WebShare.scss';
import { LitElement, html, css, unsafeCSS, TemplateResult } from 'lit';
import { ok, mail, viber, whatsapp, telegram, tw, vk } from 'vanilla-sharing';
import _styles from './WebShare.ce.scss';
import { isMobileDevice } from '@/utils/mobile';
import loadScript from '@/modules/load-script';

export interface WebShare {
    /**
     * Шерить ли в Facebook
     */
    facebook: boolean;

    /**
     * App ID для Facebook (получается в настройках бизнес-аккаунта FB)
     */
    facebookAppId: string | null;

    /**
     * Показывает, загружен ли Facebook SDK
     */
    facebookReady: boolean;

    /**
     * Шерить ли в Mail.ru
     */
    mail: boolean;

    /**
     * Использовать ли Web Share API (если доступно)
     */
    native: boolean;

    /**
     * Шерить ли в Одноклассники
     */
    ok: boolean;

    /**
     * Шерить ли в Telegram
     */
    telegram: boolean;

    /**
     * Заголовок для шеринга
     */
    title: string;

    /**
     * Шерить ли в Twitter
     */
    twitter: boolean;

    /**
     * Шерить ли в Skype
     */
    skype: boolean;

    /**
     * Показывает, загружен ли Skype SDK
     */
    skypeReady: boolean;

    /**
     * URL, который шерим
     */
    url: string;

    /**
     * Шерить ли в Viber
     */
    viber: boolean;

    /**
     * Шерить ли в VK
     */
    vk: boolean;

    /**
     * Шерить ли в Whatsapp
     */
    whatsapp: boolean;

    /**
     * Вычисленный boolean,
     * показывающий реальную возможность использовать WebShare API
     */
    _useNative: boolean;

    _darkTheme: boolean;
}

function shareFacebook(url: string) {
    if ((window as any).FB) {
        (window as any).FB.ui({
            method: 'share',
            href: url,
        });
    }
}

function shareVK(url: string) {
    vk({
        url,
        title: document.title,
    });
}

function shareTwitter(url: string) {
    tw({
        url,
        title: document.title,
    });
}

function shareOK(url: string) {
    ok({
        url,
        title: document.title,
    });
}

function shareMail(url: string) {
    mail({
        url,
        title: document.title,
    });
}

function shareViber(url: string) {
    viber({
        url,
        title: document.title,
    });
}

function shareWhatsapp(url: string) {
    whatsapp({
        url,
        title: document.title,
    });
}

function shareTelegram(url: string) {
    telegram({
        url,
        title: document.title,
    });
}

function shareSkype(url: string) {
    telegram({
        url,
        title: document.title,
    });
}

function loadSkypeSharingSDK(): Promise<void> {
    return new Promise((resolve) => {
        ((r, d, s) => {
            (r as any).loadSkypeWebSdkAsync =
                (r as any).loadSkypeWebSdkAsync ||
                function fn(p: any) {
                    const sjs = d.getElementsByTagName(s)[0] as any;
                    if (d.getElementById(p.id)) {
                        return;
                    }
                    const js = d.createElement(s) as HTMLScriptElement;
                    js.id = p.id;
                    js.src = p.scriptToLoad;
                    js.onload = p.callback;
                    sjs.parentNode.insertBefore(js, sjs);
                };
            const p = {
                scriptToLoad: 'https://swx.cdn.skype.com/shared/v/latest/skypewebsdk.js',
                id: 'skype_web_sdk',
                callback: resolve,
            };
            (r as any).loadSkypeWebSdkAsync(p);
        })(window, document, 'script');
    });
}

async function loadFacebookSharingSDK(fbAppId?: string | null) {
    if (!fbAppId) {
        console.error('Invalid Facebook App ID');
    }

    await loadScript(`https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0&appId=${fbAppId}`);
}

export class WebShare extends LitElement {
    constructor() {
        super();
        this.share = this.share.bind(this);
        this._closeOnOutsideClick = this._closeOnOutsideClick.bind(this);
        this._onChangeTheme = this._onChangeTheme.bind(this);

        this._useNative = false;
        this.native = false;

        this._darkTheme = localStorage.getItem('theme') === 'dark';

        this.title = document.title;
        this.url = window.location.href;

        this.facebookReady = false;
        this.skypeReady = false;

        this.facebook = false;
        this.mail = false;
        this.ok = false;
        this.telegram = true;
        this.twitter = false;
        this.skype = false;
        this.viber = true;
        this.vk = true;
        this.whatsapp = true;

        this.facebookAppId = this.getAttribute('facebook-app-id');
    }

    static get styles() {
        return css`
            ${unsafeCSS(_styles)}
        `;
    }

    static get properties() {
        return {
            facebook: {
                type: Boolean,
            },
            facebookAppId: {
                type: String,
            },
            mail: {
                type: Boolean,
            },
            native: {
                type: Boolean,
            },
            ok: {
                type: Boolean,
            },
            telegram: {
                type: Boolean,
            },
            twitter: {
                type: Boolean,
            },
            skype: {
                type: Boolean,
            },
            viber: {
                type: Boolean,
            },
            vk: {
                type: Boolean,
            },
            whatsapp: {
                type: Boolean,
            },
            _useNative: {
                type: Boolean,
            },
            _darkTheme: {
                type: Boolean,
            },
        };
    }

    connectedCallback() {
        super.connectedCallback();
        this._useNative = this.native && 'share' in navigator && isMobileDevice();

        if (!this._useNative) {
            if (this.facebook) {
                loadFacebookSharingSDK(this.facebookAppId).then(() => {
                    this.facebookReady = true;
                });
            }

            if (this.skype) {
                loadSkypeSharingSDK().then(() => {
                    this.skypeReady = true;
                });
            }
        }

        document.addEventListener('change-theme', this._onChangeTheme);
        document.addEventListener('click', this._closeOnOutsideClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._closeOnOutsideClick);
        document.removeEventListener('change-theme', this._onChangeTheme);
    }

    async share() {
        if (this._useNative) {
            await navigator.share({ text: '', title: this.title, url: this.url });
        } else {
            this.renderRoot.querySelector('.list')?.classList.toggle('is-open');
        }
    }

    private _closeOnOutsideClick(event: any) {
        const el = event.target.closest('app-web-share');

        if (!el) {
            this.renderRoot.querySelector('.list')?.classList.remove('is-open');
        }
    }

    private _onChangeTheme(event: any) {
        const { theme } = event.detail;
        this._darkTheme = theme === 'dark';
    }

    /**
     *
     * Lit-шаблон для элемента списка
     *
     * @param iconHtml Lit-шаблон SVG-иконки
     * @param label Название соц. сети
     * @param sharingFn Релевантная функция шеринга
     * @returns TemplateResult<1>
     */
    li(iconHtml: TemplateResult<1>, label: string, sharingFn: any) {
        return html`
            <li class="list__item">
                <button
                    type="button"
                    class="list-btn"
                    aria-label="Поделиться в ${label}"
                    @click="${() => sharingFn(this.url)}"
                >
                    ${iconHtml}
                </button>
            </li>
        `;
    }

    render() {
        return html`
            <button
                type="button"
                class=" ${this._useNative ? '' : 'list-btn--main'}
                ${this._darkTheme ? 'share-btn--dark' : 'share-btn'} btn btn-outline-dark"
                aria-label="Поделиться"
                @click="${this.share}"
            >
                Поделиться
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M6.584 3.03346V0.508789L9.32975 3.25454L11.5312 5.45604L9.29067 7.32329L6.584 9.57904V7.10862C1.83217 6.75046 0.167334 9.50029 0.167334 9.50029C0.167334 7.78704 0.3085 6.00904 1.65542 4.66271C3.22108 3.09646 5.4885 2.98562 6.584 3.03346Z"
                        fill="#1B1B1B"
                    />
                </svg>
            </button>
            ${this._useNative
                ? ''
                : html`
                      <ul class="list">
                          ${this.viber
                              ? this.li(
                                    html`<svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M17.3438 1.94921C16.8477 1.49218 14.8399 0.0351481 10.3633 0.0156168C10.3633 0.0156168 5.08595 -0.300789 2.51563 2.05859C1.08595 3.48827 0.58204 5.58593 0.527353 8.18359C0.472665 10.7812 0.406259 15.6484 5.09766 16.9687H5.10157L5.09766 18.9844C5.09766 18.9844 5.06641 19.8008 5.60548 19.9648C6.25391 20.168 6.63673 19.5469 7.25782 18.8789C7.59767 18.5117 8.06642 17.9726 8.42188 17.5625C11.6328 17.832 14.0977 17.2148 14.3789 17.125C15.0274 16.9141 18.6953 16.4453 19.2891 11.5781C19.9063 6.55468 18.9922 3.3828 17.3438 1.94921ZM17.8867 11.2109C17.3828 15.2734 14.4102 15.5312 13.8633 15.707C13.6289 15.7812 11.4609 16.3203 8.73829 16.1445C8.73829 16.1445 6.70704 18.5937 6.07423 19.2305C5.8672 19.4375 5.64063 19.418 5.64454 19.0078C5.64454 18.7383 5.66016 15.6601 5.66016 15.6601C5.65626 15.6601 5.65626 15.6601 5.66016 15.6601C1.6836 14.5586 1.91798 10.4141 1.96095 8.24609C2.00392 6.07812 2.41407 4.30077 3.62501 3.10546C5.80079 1.1328 10.2813 1.42577 10.2813 1.42577C14.0664 1.4414 15.8789 2.58202 16.3008 2.96484C17.6953 4.16015 18.4063 7.01952 17.8867 11.2109ZM12.457 8.05468C12.4727 8.39062 11.9688 8.41405 11.9531 8.07812C11.9102 7.21874 11.5078 6.80077 10.6797 6.7539C10.3438 6.73437 10.375 6.23046 10.707 6.24999C11.7969 6.30859 12.4024 6.93359 12.457 8.05468ZM13.25 8.49609C13.2891 6.83984 12.2539 5.54296 10.2891 5.39843C9.95704 5.37499 9.9922 4.87109 10.3242 4.89452C12.5899 5.05859 13.7969 6.61718 13.7539 8.5078C13.75 8.84374 13.2422 8.82812 13.25 8.49609ZM15.0859 9.01952C15.0899 9.35546 14.582 9.35937 14.582 9.02343C14.5586 5.83984 12.4375 4.10546 9.86329 4.08593C9.53126 4.08202 9.53126 3.58202 9.86329 3.58202C12.7422 3.60155 15.0586 5.58984 15.0859 9.01952ZM14.6445 12.8516V12.8594C14.2227 13.6016 13.4336 14.4219 12.6211 14.1601L12.6133 14.1484C11.7891 13.918 9.84766 12.918 8.6211 11.9414C7.98829 11.4414 7.41017 10.8516 6.96485 10.2851C6.56251 9.78124 6.15626 9.18359 5.76173 8.46484C4.9297 6.96093 4.7461 6.28905 4.7461 6.28905C4.48438 5.47655 5.30079 4.68749 6.04688 4.26562H6.0547C6.41407 4.07812 6.75782 4.14062 6.98829 4.41796C6.98829 4.41796 7.47267 4.99609 7.6797 5.28124C7.87501 5.54687 8.13673 5.97265 8.27345 6.21093C8.51173 6.63671 8.36329 7.0703 8.12892 7.24999L7.66017 7.62499C7.42188 7.8164 7.45313 8.17187 7.45313 8.17187C7.45313 8.17187 8.14845 10.8008 10.7461 11.4648C10.7461 11.4648 11.1016 11.4961 11.293 11.2578L11.668 10.7891C11.8477 10.5547 12.2813 10.4062 12.707 10.6445C13.2813 10.9687 14.0117 11.4726 14.4961 11.9297C14.7695 12.1523 14.832 12.4922 14.6445 12.8516Z"
                                            fill="#1B1B1B"
                                        />
                                    </svg> `,
                                    'Viber',
                                    shareViber,
                                )
                              : ''}
                          ${this.whatsapp
                              ? this.li(
                                    html`<svg
                                        width="18"
                                        height="21"
                                        viewBox="0 0 18 21"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M15.304 3.98262C13.6205 2.25996 11.3786 1.3125 8.99598 1.3125C4.07812 1.3125 0.0763393 5.39766 0.0763393 10.418C0.0763393 12.0217 0.486161 13.5885 1.26562 14.9707L0 19.6875L4.72902 18.4201C6.0308 19.1461 7.49732 19.5275 8.99196 19.5275H8.99598C13.9098 19.5275 18 15.4424 18 10.4221C18 7.98984 16.9875 5.70527 15.304 3.98262ZM8.99598 17.9936C7.66205 17.9936 6.35625 17.6285 5.2192 16.9395L4.95 16.7754L2.14554 17.526L2.89286 14.7328L2.71607 14.4457C1.97277 13.2398 1.58304 11.8494 1.58304 10.418C1.58304 6.24668 4.90982 2.85059 9 2.85059C10.9808 2.85059 12.8411 3.63809 14.2393 5.06953C15.6375 6.50098 16.4973 8.4 16.4933 10.4221C16.4933 14.5975 13.0821 17.9936 8.99598 17.9936ZM13.0621 12.3252C12.8411 12.2104 11.7442 11.6607 11.5393 11.5869C11.3344 11.509 11.1857 11.4721 11.0371 11.7018C10.8884 11.9314 10.4625 12.44 10.3299 12.5959C10.2013 12.7477 10.0688 12.7682 9.84777 12.6533C8.53795 11.9848 7.67813 11.4598 6.81429 9.94629C6.58527 9.54434 7.0433 9.57305 7.4692 8.70352C7.54152 8.55176 7.50536 8.42051 7.44911 8.30566C7.39286 8.19082 6.94688 7.07109 6.76205 6.61582C6.58125 6.17285 6.39643 6.23438 6.25982 6.22617C6.13125 6.21797 5.98259 6.21797 5.83393 6.21797C5.68527 6.21797 5.4442 6.27539 5.23929 6.50098C5.03437 6.73066 4.45982 7.28027 4.45982 8.4C4.45982 9.51973 5.25938 10.6025 5.36786 10.7543C5.48036 10.9061 6.93884 13.2029 9.17679 14.1914C10.5911 14.8148 11.1455 14.8682 11.8527 14.7615C12.2826 14.6959 13.1705 14.2119 13.3554 13.6787C13.5402 13.1455 13.5402 12.6902 13.4839 12.5959C13.4317 12.4934 13.283 12.4359 13.0621 12.3252Z"
                                            fill="#1B1B1B"
                                        />
                                    </svg>`,
                                    'Whatsapp',
                                    shareWhatsapp,
                                )
                              : ''}
                          ${this.facebook && this.facebookReady && this.facebookAppId
                              ? this.li(
                                    html`<svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M0.00878906 8.88932C0.00878906 3.97988 3.98794 0 8.89646 0C13.805 0 17.7841 3.97988 17.7841 8.88932C17.7841 13.5996 14.1212 17.4542 9.48897 17.7592V10.6672H11.859V9.48194H9.48897V7.70407C9.48897 6.72219 10.2848 5.92621 11.2665 5.92621H11.859V4.74097H11.2665C9.63033 4.74097 8.30395 6.0676 8.30395 7.70407V9.48194H5.9339V10.6672H8.30395V17.7592C3.67168 17.4542 0.00878906 13.5996 0.00878906 8.88932Z"
                                            fill="#94A1BD"
                                        />
                                    </svg>`,
                                    'Facebook',
                                    shareFacebook,
                                )
                              : ''}
                          ${this.vk
                              ? this.li(
                                    html`<svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1.40583 1.40583C0 2.81167 0 5.0746 0 9.6V10.4C0 14.9254 0 17.1883 1.40583 18.5942C2.81167 20 5.0746 20 9.6 20H10.4C14.9254 20 17.1883 20 18.5942 18.5942C20 17.1883 20 14.9254 20 10.4V9.6C20 5.0746 20 2.81167 18.5942 1.40583C17.1883 0 14.9254 0 10.4 0H9.6C5.0746 0 2.81167 0 1.40583 1.40583ZM3.375 6.08335H5.65835C5.73335 9.9 7.41665 11.5167 8.75 11.85V6.08335H10.9V9.375C12.2167 9.23335 13.6 7.73335 14.0667 6.08335H16.2167C16.0408 6.93906 15.6902 7.74924 15.1869 8.46335C14.6836 9.17741 14.0384 9.77996 13.2917 10.2333C14.1254 10.6473 14.8619 11.2335 15.4523 11.9532C16.0427 12.6729 16.4737 13.5097 16.7167 14.4083H14.35C14.1319 13.6278 13.6881 12.929 13.0743 12.3997C12.4606 11.8705 11.7042 11.5343 10.9 11.4333V14.4083H10.6417C6.08335 14.4083 3.48333 11.2833 3.375 6.08335Z"
                                            fill="#1B1B1B"
                                        />
                                    </svg> `,
                                    'VK',
                                    shareVK,
                                )
                              : ''}
                          ${this.twitter
                              ? this.li(
                                    html`<svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M0.00878906 8.88932C0.00878906 3.97988 3.98794 0 8.89646 0C13.805 0 17.7841 3.97988 17.7841 8.88932C17.7841 13.5996 14.1212 17.4542 9.48897 17.7592V10.6672H11.859V9.48194H9.48897V7.70407C9.48897 6.72219 10.2848 5.92621 11.2665 5.92621H11.859V4.74097H11.2665C9.63033 4.74097 8.30395 6.0676 8.30395 7.70407V9.48194H5.9339V10.6672H8.30395V17.7592C3.67168 17.4542 0.00878906 13.5996 0.00878906 8.88932Z"
                                            fill="#94A1BD"
                                        />
                                    </svg>`,
                                    'Twitter',
                                    shareTwitter,
                                )
                              : ''}
                          ${this.ok
                              ? this.li(
                                    html`<svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M0.00878906 8.88932C0.00878906 3.97988 3.98794 0 8.89646 0C13.805 0 17.7841 3.97988 17.7841 8.88932C17.7841 13.5996 14.1212 17.4542 9.48897 17.7592V10.6672H11.859V9.48194H9.48897V7.70407C9.48897 6.72219 10.2848 5.92621 11.2665 5.92621H11.859V4.74097H11.2665C9.63033 4.74097 8.30395 6.0676 8.30395 7.70407V9.48194H5.9339V10.6672H8.30395V17.7592C3.67168 17.4542 0.00878906 13.5996 0.00878906 8.88932Z"
                                            fill="#94A1BD"
                                        />
                                    </svg>`,
                                    'Odnoklassniki',
                                    shareOK,
                                )
                              : ''}
                          ${this.telegram
                              ? this.li(
                                    html`<svg
                                        width="19"
                                        height="20"
                                        viewBox="0 0 19 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_3157_50922)">
                                            <path
                                                d="M9.5 0.3125C4.25328 0.3125 0 4.64973 0 10C0 15.3503 4.25328 19.6875 9.5 19.6875C14.7467 19.6875 19 15.3503 19 10C19 4.64973 14.7467 0.3125 9.5 0.3125ZM13.9034 6.90078C13.7604 8.43262 13.1418 12.1499 12.827 13.8656C12.6938 14.5916 12.4316 14.835 12.1778 14.8588C11.6262 14.9106 11.2072 14.487 10.6728 14.1298C9.83671 13.5709 9.36436 13.223 8.55272 12.6776C7.61479 12.0473 8.22282 11.7011 8.75735 11.1346C8.89725 10.9865 11.328 8.73191 11.375 8.52738C11.3809 8.5018 11.3865 8.40629 11.3308 8.35613C11.2751 8.30598 11.1933 8.32297 11.1341 8.3366C11.0503 8.35603 9.71455 9.25631 7.12695 11.0375C6.74785 11.3029 6.40444 11.4323 6.09674 11.4255C5.75754 11.418 5.10506 11.23 4.61999 11.0691C4.02505 10.872 3.5522 10.7677 3.59338 10.4328C3.61483 10.2583 3.85041 10.0799 4.30013 9.89762C7.06958 8.6672 8.91631 7.856 9.84031 7.46402C12.4786 6.345 13.0268 6.15062 13.3841 6.14414C13.4627 6.14281 13.6384 6.16266 13.7522 6.25684C13.8279 6.32393 13.8762 6.41755 13.8874 6.51918C13.9067 6.64537 13.9121 6.77337 13.9034 6.90078Z"
                                                fill="#1B1B1B"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3157_50922">
                                                <rect width="19" height="20" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg> `,
                                    'Telegram',
                                    shareTelegram,
                                )
                              : ''}
                          ${this.mail
                              ? this.li(
                                    html`<svg
                                        width="19"
                                        height="16"
                                        viewBox="0 0 19 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M18.2985 0.772295C18.3331 0.559721 18.2553 0.344279 18.0942 0.20603C17.9331 0.0677815 17.7126 0.027394 17.5148 0.0998766L0.924484 6.17884C0.700955 6.26074 0.546639 6.4716 0.532333 6.71468C0.518026 6.95775 0.64649 7.18613 0.858788 7.29504L5.59888 9.72662C5.78906 9.82418 6.01561 9.80971 6.19252 9.6887L10.1273 6.99743L7.77123 10.0189C7.66749 10.152 7.62293 10.3236 7.64841 10.492C7.67388 10.6603 7.76706 10.81 7.90524 10.9045L15.0154 15.7677C15.1827 15.8821 15.3951 15.9017 15.5796 15.8197C15.7641 15.7377 15.8954 15.5653 15.9285 15.3618L18.2985 0.772295Z"
                                            fill="#94A1BD"
                                        />
                                    </svg>`,
                                    'Mail',
                                    shareMail,
                                )
                              : ''}
                          ${this.skype && this.skypeReady
                              ? this.li(
                                    html`<svg
                                        width="19"
                                        height="16"
                                        viewBox="0 0 19 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M18.2985 0.772295C18.3331 0.559721 18.2553 0.344279 18.0942 0.20603C17.9331 0.0677815 17.7126 0.027394 17.5148 0.0998766L0.924484 6.17884C0.700955 6.26074 0.546639 6.4716 0.532333 6.71468C0.518026 6.95775 0.64649 7.18613 0.858788 7.29504L5.59888 9.72662C5.78906 9.82418 6.01561 9.80971 6.19252 9.6887L10.1273 6.99743L7.77123 10.0189C7.66749 10.152 7.62293 10.3236 7.64841 10.492C7.67388 10.6603 7.76706 10.81 7.90524 10.9045L15.0154 15.7677C15.1827 15.8821 15.3951 15.9017 15.5796 15.8197C15.7641 15.7377 15.8954 15.5653 15.9285 15.3618L18.2985 0.772295Z"
                                            fill="#94A1BD"
                                        />
                                    </svg>`,
                                    'Skype',
                                    shareSkype,
                                )
                              : ''}
                      </ul>
                  `}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'app-web-share': WebShare;
    }
}
